import { useEffect, useState } from "react";
import { Source, Layer } from "react-map-gl";
import PalmMap from "./PalmMap";

const fmt3 = (v) => parseFloat(v).toFixed(3);

const MAPLE_INTERACTIVE_LAYER_IDS = [
    "maple-span-bams-layer",
    "maple-weather-site-bams-layer",
    "maple-span-bam-diffs-layer",
    "maple-bam-outliers-layer",
    "maple-device-failures-layer",
    "maple-span-failures-layer",
];

export default function MapleMap({ mapleSpanBams, mapleWeatherSiteBams, mapleSpanBamDiffs, mapleNonconformity, ...palmProps }) {
    const [bamOutliersGeoJSON, setBamOutliersGeoJSON] = useState(null);
    const [deviceFailuresGeoJSON, setDeviceFailuresGeoJSON] = useState(null);
    const [spanFailuresGeoJSON, setSpanFailuresGeoJSON] = useState(null);
    const [spanBamsGeoJSON, setSpanBamsGeoJSON] = useState(null);
    const [weatherSiteBamsGeoJSON, setWeatherSiteBamsGeoJSON] = useState(null);
    const [spanBamDiffsGeoJSON, setSpanBamDiffsGeoJSON] = useState(null);

    useEffect(() => {
        if (!mapleNonconformity) {
            setBamOutliersGeoJSON(null);
            setDeviceFailuresGeoJSON(null);
            setSpanFailuresGeoJSON(null);
            return;
        }

        setBamOutliersGeoJSON({
            type: "FeatureCollection",
            features: (mapleNonconformity.bam_outliers || []).map((o) => ({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [parseFloat(o.longitude), parseFloat(o.latitude)],
                },
                properties: {
                    description: `BAM Outlier — Site ${o.weather_site_id}: ${fmt3(o.value)} (${o.type})`,
                },
            })),
        });

        setDeviceFailuresGeoJSON({
            type: "FeatureCollection",
            features: (mapleNonconformity.device_health_failures || []).map((f) => ({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [parseFloat(f.longitude), parseFloat(f.latitude)],
                },
                properties: {
                    description: `Device Health Failure — Site ${f.weather_site_id}${f.days_data_last_90 != null ? ` | Days data last 90: ${f.days_data_last_90}` : ""}`,
                },
            })),
        });

        setSpanFailuresGeoJSON({
            type: "FeatureCollection",
            features: (mapleNonconformity.span_validation_failures || []).map((s) => ({
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [parseFloat(s.mid_longitude), parseFloat(s.mid_latitude)],
                },
                properties: {
                    description: `Span ${s.span_id}: ${fmt3(s.change_percentage * 100)}% change`,
                },
            })),
        });
    }, [mapleNonconformity]);

    useEffect(() => {
        if (!mapleWeatherSiteBams || mapleWeatherSiteBams.length === 0) {
            setWeatherSiteBamsGeoJSON(null);
            return;
        }

        setWeatherSiteBamsGeoJSON({
            type: "FeatureCollection",
            features: mapleWeatherSiteBams
                .filter((b) => b.latitude && b.longitude)
                .map((b) => ({
                    type: "Feature",
                    geometry: {
                        type: "Point",
                        coordinates: [parseFloat(b.longitude), parseFloat(b.latitude)],
                    },
                    properties: {
                        description: `Site ${b.weather_site_id} — BAM: ${fmt3(b.bam)} (${b.rating_methodology})`,
                        bam: parseFloat(b.bam),
                    },
                })),
        });
    }, [mapleWeatherSiteBams]);

    useEffect(() => {
        if (!mapleSpanBams || mapleSpanBams.length === 0) {
            setSpanBamsGeoJSON(null);
            return;
        }

        setSpanBamsGeoJSON({
            type: "FeatureCollection",
            features: mapleSpanBams
                .filter((b) => b.latitude && b.longitude)
                .map((b) => ({
                    type: "Feature",
                    geometry: {
                        type: "Point",
                        coordinates: [parseFloat(b.longitude), parseFloat(b.latitude)],
                    },
                    properties: {
                        description: [
                            `Span ${b.span_id} — BAM: ${fmt3(b.bam)} (${b.rating_methodology})`,
                            b.weather_site_1_id != null
                                ? `Site 1: #${b.weather_site_1_id}, BAM: ${fmt3(b.weather_site_1_bam)}, ${fmt3(b.weather_site_1_distance_m / 1000)} km`
                                : null,
                            b.weather_site_2_id != null
                                ? `Site 2: #${b.weather_site_2_id}, BAM: ${fmt3(b.weather_site_2_bam)}, ${fmt3(b.weather_site_2_distance_m / 1000)} km`
                                : null,
                        ]
                            .filter(Boolean)
                            .join("\n"),
                        bam: parseFloat(b.bam),
                    },
                })),
        });
    }, [mapleSpanBams]);

    useEffect(() => {
        if (!mapleSpanBamDiffs || mapleSpanBamDiffs.length === 0) {
            setSpanBamDiffsGeoJSON(null);
            return;
        }

        setSpanBamDiffsGeoJSON({
            type: "FeatureCollection",
            features: mapleSpanBamDiffs.map((d) => {
                const absPct = Math.abs(d.pctChange);
                const sign = d.diff >= 0 ? "+" : "";
                return {
                    type: "Feature",
                    geometry: {
                        type: "Point",
                        coordinates: [parseFloat(d.longitude), parseFloat(d.latitude)],
                    },
                    properties: {
                        description: `Span ${d.span_id} — BAM: ${fmt3(d.prevBam)} → ${fmt3(d.bam)} (${sign}${fmt3(d.diff)}, ${sign}${fmt3(d.pctChange)}%)`,
                        pctChange: d.pctChange,
                        significant: absPct >= 15,
                    },
                };
            }),
        });
    }, [mapleSpanBamDiffs]);

    return (
        <PalmMap {...palmProps} additionalInteractiveLayerIds={MAPLE_INTERACTIVE_LAYER_IDS}>
            {spanBamsGeoJSON && (
                <Source id="maple-span-bams" type="geojson" data={spanBamsGeoJSON}>
                    <Layer
                        id="maple-span-bams-layer"
                        type="circle"
                        paint={{
                            "circle-radius": 7,
                            "circle-color": [
                                "interpolate",
                                ["linear"],
                                ["get", "bam"],
                                0.5, "#d32f2f",
                                0.8, "#ff9800",
                                1.0, "#ffeb3b",
                                1.3, "#4caf50",
                                2.0, "#1976d2",
                            ],
                            "circle-opacity": 0.85,
                            "circle-stroke-width": 1,
                            "circle-stroke-color": "#fff",
                        }}
                    />
                </Source>
            )}
            {weatherSiteBamsGeoJSON && (
                <Source id="maple-weather-site-bams" type="geojson" data={weatherSiteBamsGeoJSON}>
                    <Layer
                        id="maple-weather-site-bams-layer"
                        type="symbol"
                        layout={{
                            "text-field": "■",
                            "text-size": 18,
                            "text-allow-overlap": true,
                        }}
                        paint={{
                            "text-color": [
                                "interpolate",
                                ["linear"],
                                ["get", "bam"],
                                0.5, "#d32f2f",
                                0.8, "#ff9800",
                                1.0, "#ffeb3b",
                                1.3, "#4caf50",
                                2.0, "#1976d2",
                            ],
                            "text-halo-color": "#fff",
                            "text-halo-width": 1.5,
                        }}
                    />
                </Source>
            )}
            {spanBamDiffsGeoJSON && (
                <Source id="maple-span-bam-diffs" type="geojson" data={spanBamDiffsGeoJSON}>
                    <Layer
                        id="maple-span-bam-diffs-layer"
                        type="circle"
                        paint={{
                            "circle-radius": [
                                "case",
                                ["get", "significant"],
                                12,
                                7,
                            ],
                            "circle-color": [
                                "interpolate",
                                ["linear"],
                                ["get", "pctChange"],
                                -30, "#d32f2f",
                                -15, "#ff6d00",
                                0, "#e0e0e0",
                                15, "#43a047",
                                30, "#1b5e20",
                            ],
                            "circle-opacity": 0.9,
                            "circle-stroke-width": [
                                "case",
                                ["get", "significant"],
                                2.5,
                                1,
                            ],
                            "circle-stroke-color": [
                                "case",
                                ["get", "significant"],
                                "#fff",
                                "rgba(255,255,255,0.5)",
                            ],
                        }}
                    />
                </Source>
            )}
            {mapleNonconformity && bamOutliersGeoJSON && (
                <Source id="maple-bam-outliers" type="geojson" data={bamOutliersGeoJSON}>
                    <Layer
                        id="maple-bam-outliers-layer"
                        type="circle"
                        paint={{
                            "circle-radius": 10,
                            "circle-color": "#FF8C00",
                            "circle-opacity": 0.85,
                            "circle-stroke-width": 1,
                            "circle-stroke-color": "#fff",
                        }}
                    />
                </Source>
            )}
            {mapleNonconformity && deviceFailuresGeoJSON && (
                <Source id="maple-device-failures" type="geojson" data={deviceFailuresGeoJSON}>
                    <Layer
                        id="maple-device-failures-layer"
                        type="circle"
                        paint={{
                            "circle-radius": 10,
                            "circle-color": "#CC0000",
                            "circle-opacity": 0.85,
                            "circle-stroke-width": 1,
                            "circle-stroke-color": "#fff",
                        }}
                    />
                </Source>
            )}
            {mapleNonconformity && spanFailuresGeoJSON && (
                <Source id="maple-span-failures" type="geojson" data={spanFailuresGeoJSON}>
                    <Layer
                        id="maple-span-failures-layer"
                        type="circle"
                        paint={{
                            "circle-radius": 10,
                            "circle-color": "#FFD700",
                            "circle-opacity": 0.85,
                            "circle-stroke-width": 1,
                            "circle-stroke-color": "#555",
                        }}
                    />
                </Source>
            )}
        </PalmMap>
    );
}
